// ランキング API クライアント。
// 本番は同一ドメインの /api を叩く。ローカルフルスタック検証時は VITE_API_BASE で差し替え可能。
import { getPlayerId, getNickname, getCountry, getLanguage } from './playerId';
import type {
  StageLeaderboardEntry,
  StarsLeaderboardEntry,
  GlobalLeaderboardEntry,
  CountryLeaderboardEntry,
  MedalLeaderboardEntry,
  BalanceLeaderboardEntry,
  PopularGamesResult,
  SubmitScoreResult,
} from '@/platform/types/leaderboard';

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

// ⚠️ ゲームキーの定数はここに置かない。**横断モジュールが特定ゲームを既定値にすると、
//    2本目のゲームが「省略したら二角取りに書かれる」事故を踏む**。各ゲームが
//    `src/games/<key>/gameKey.ts` で自分のキーを持ち、呼び出し側が明示的に渡す。

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'content-type': 'application/json' },
    // セッション Cookie を載せる。同一オリジンなので既定でも載るが、
    // **削除の所有者チェック（実装順序(7)）がこれに依存している**ので明示しておく。
    credentials: 'same-origin',
    ...init,
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `request failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

/** プレイヤー登録/更新（ニックネーム＋国）。国は任意。 */
export async function registerPlayer(
  nickname: string,
  country?: string | null,
  lang?: string | null,
): Promise<void> {
  await api('/player', {
    method: 'POST',
    body: JSON.stringify({
      id: getPlayerId(),
      nickname,
      country: country ?? getCountry(),
      // 表示言語も一緒に保存する（`players.language_code`）。国とは独立した属性で、
      // 本認証で複数端末が同じ id を共有すれば、そのまま言語もクロスデバイスになる。
      lang: lang ?? getLanguage(),
    }),
  });
}

/** 後方互換: ニックネームのみ登録（国は保存済みの値を送る）。 */
export async function registerNickname(nickname: string): Promise<void> {
  await registerPlayer(nickname);
}

/**
 * サーバー上のこのプレイヤーのデータを全消去する（プライバシーポリシー 8. / GDPR 消去権）。
 * 端末内のデータは `clearLocalPlayerData()` が別に消す＝**両方消して初めて「削除」**。
 * 未登録IDでも `ok` が返る（消えている状態になった、は満たされている）。
 *
 * 🔴 **`unauthorized` を `failed` に混ぜないこと**（実装順序(7)で所有者チェックが入った）。
 *    パスキーを持っている人はセッションが要るので、切れていると 401 になる。
 *    これを「通信の失敗」と同じ文言にすると、**再試行しても永久に成功しない**のに
 *    「もう一度お試しください」と促すことになる（パスキー登録の `already` と同じ失敗の形）。
 */
export async function deletePlayerRecord(): Promise<'ok' | 'unauthorized' | 'failed'> {
  try {
    const res = await fetch(`${API_BASE}/player?id=${encodeURIComponent(getPlayerId())}`, {
      method: 'DELETE',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
    });
    if (res.ok) return 'ok';
    return res.status === 401 ? 'unauthorized' : 'failed';
  } catch {
    return 'failed';
  }
}

/**
 * スコアを送信し、ベスト記録と現在順位を返す。
 *
 * 💡 **戻りの順位を画面に出すかは、ゲームの型で分かれている**（2026-09-05 に確認・現状維持と決定）:
 *   - **1面完結型は出す**（二角取り / Triangle Sweeper / トランプ / 2048 / スライド）
 *     ＝ `const res = await submitScore(...)` を待ってクリアダイアログに載せる。
 *   - **連続プレイ型は出さない**（箱プッシュ / ノノグラム / ナンプレ / カラーソート / さめがめ）
 *     ＝ `void submitScore(...).catch(...)` で捨てる。**毎面サーバーの応答を待つとテンポが壊れる**ため。
 *   ⚠️ **5対5で割れているのは事故ではなく線引き**。新しいゲームを足すときはどちらの型かで決める。
 */
export async function submitScore(
  stageKey: string,
  score: number,
  stars: number,
  gameKey: string,
): Promise<SubmitScoreResult> {
  const nickname = getNickname() ?? 'Player';
  return api<SubmitScoreResult>('/score', {
    method: 'POST',
    body: JSON.stringify({
      playerId: getPlayerId(),
      nickname,
      country: getCountry(),
      gameKey,
      stageKey,
      score,
      stars,
    }),
  });
}

/**
 * 「自分の行」を光らせるためのクエリ。**自分のIDを自分で送るだけ**で、
 * サーバーは他人のIDを返さない（詳しくは functions/api/leaderboard.ts の注記）。
 */
function meQuery(): string {
  return `&me=${encodeURIComponent(getPlayerId())}`;
}

/**
 * SQLite の `CASE WHEN` は 1/0 を返すので真偽値へ均す。
 * ⚠️ ここを忘れると `0` が falsy なので**たまたま動いてしまい**、後で
 * `isMe === true` の比較を書いた瞬間に静かに壊れる。
 */
function withIsMe<T extends { isMe: unknown }>(entries: T[]): (Omit<T, 'isMe'> & { isMe: boolean })[] {
  return entries.map((e) => ({ ...e, isMe: !!e.isMe }));
}

/** ステージ別スコアランキング上位を取得。 */
export async function fetchStageLeaderboard(
  stageKey: string,
  gameKey: string,
  limit = 50,
): Promise<StageLeaderboardEntry[]> {
  const data = await api<{ entries: StageLeaderboardEntry[] }>(
    `/leaderboard?type=stage&game=${encodeURIComponent(gameKey)}&stage=${encodeURIComponent(stageKey)}&limit=${limit}${meQuery()}`,
  );
  return withIsMe(data.entries);
}

/** スター合計ランキング上位を取得（後方互換）。 */
export async function fetchStarsLeaderboard(limit = 50): Promise<StarsLeaderboardEntry[]> {
  const data = await api<{ entries: StarsLeaderboardEntry[] }>(
    `/leaderboard?type=stars&limit=${limit}${meQuery()}`,
  );
  return withIsMe(data.entries);
}

/**
 * プレイヤー別グローバルランキング（★合計）を取得。
 * gameKey を渡すとそのゲームのみ、省略で全ゲーム横断。
 */
export async function fetchGlobalLeaderboard(
  limit = 20,
  gameKey?: string,
): Promise<GlobalLeaderboardEntry[]> {
  const q = gameKey ? `&game=${encodeURIComponent(gameKey)}` : '';
  const data = await api<{ entries: GlobalLeaderboardEntry[] }>(
    `/leaderboard?type=global&limit=${limit}${q}${meQuery()}`,
  );
  return withIsMe(data.entries);
}

/**
 * 実績メダルランキングを取得。gameKey 省略で全ゲーム横断。
 *
 * 🔴 **鍵（パスキー）を持つ人しか返らない**（docs/CURRENCY.md 決定7）。
 *   ⚠️ **空で返るのは異常ではない**——「まだ誰も鍵を作っていない」だけ。
 *   ★ランキング（`fetchGlobalLeaderboard`）は今どおり全員ぶん返るので、
 *   **両方を並べると「載っていない人がいる」ことが見える**。
 */
/**
 * 手持ちメダルランキング（実績 ＋ ポーカーの増減・決定3）。
 *
 * ⚠️ **ゲームでは絞れない**——台帳はゲームに属さないので、サーバーは `game` 付きを 400 で断る。
 *    ここでも引数を取らないことで、呼び出し側が渡せないようにしてある。
 */
export async function fetchBalanceLeaderboard(limit = 20): Promise<BalanceLeaderboardEntry[]> {
  const data = await api<{ entries: BalanceLeaderboardEntry[] }>(
    `/leaderboard?type=balance&limit=${limit}${meQuery()}`,
  );
  return withIsMe(data.entries);
}

export async function fetchMedalLeaderboard(
  limit = 20,
  gameKey?: string,
): Promise<MedalLeaderboardEntry[]> {
  const q = gameKey ? `&game=${encodeURIComponent(gameKey)}` : '';
  const data = await api<{ entries: MedalLeaderboardEntry[] }>(
    `/leaderboard?type=medals&limit=${limit}${q}${meQuery()}`,
  );
  return withIsMe(data.entries);
}

/** 国別ランキング（★合計・参加人数）を取得。gameKey 省略で全ゲーム横断。 */
export async function fetchCountryLeaderboard(
  limit = 20,
  gameKey?: string,
): Promise<CountryLeaderboardEntry[]> {
  const q = gameKey ? `&game=${encodeURIComponent(gameKey)}` : '';
  const data = await api<{ entries: CountryLeaderboardEntry[] }>(
    `/leaderboard?type=country&limit=${limit}${q}`,
  );
  return data.entries;
}

/**
 * 人気ゲームランキング（**プレイ率**の高い順）を取得。
 *
 * ⚠️ **ゲーム絞りの引数を持たない**。「どのゲームが遊ばれているか」を並べる表なので、
 *    1本に絞ると必ず1行の表になる＝意味を成さない。
 * 🔴 **`totalPlays` を捨てないこと**。率の分母は `limit` の外側で数えた総回数で、
 *    返ってきた行だけを足したものとは限らない（上位 N 本に切られている場合）。
 */
export async function fetchPopularGames(limit = 10): Promise<PopularGamesResult> {
  const data = await api<PopularGamesResult>(`/leaderboard?type=games&limit=${limit}`);
  return { totalPlays: data.totalPlays ?? 0, entries: data.entries ?? [] };
}
